import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import shopifyBag from "../../../../assets/img/shopify-bag.svg";
import { ButtonT1 } from "../../../../components/atoms/Button/Button";
import { FormFieldError } from "../../../../components/atoms/FormFiledError";
import { useNotification } from "../../../../hooks/useNotification";
import {
  useGetSignupMetaQuery,
  useStartSignupMutation,
} from "../../../../store/apiSlice/authApiSlice";
import { useGetShopifyInstallClaimQuery } from "../../../../store/apiSlice/shopifyApiSlice";
import {
  SignupBrandRowDiv,
  SignupFormDiv,
  SignupHeadingDiv,
  SignupPanelDiv,
  SignupShellDiv,
  SignupShopPillSpan,
  SignupStatusCardDiv,
} from "../index.styles";

const TURNSTILE_SCRIPT = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

const StatusScreen = ({ title, description, action }) => (
  <SignupShellDiv>
    <SignupStatusCardDiv>
      <img src={shopifyBag} alt="Shopify" />
      <h1>{title}</h1>
      <p>{description}</p>
      {action}
    </SignupStatusCardDiv>
  </SignupShellDiv>
);

/**
 * Shopify App Store self-signup form (claim-gated).
 */
export const ShopifySignupPage = () => {
  const notify = useNotification();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const claim = searchParams.get("claim") || "";

  const {
    data: claimData,
    isLoading: claimLoading,
    isError: claimError,
  } = useGetShopifyInstallClaimQuery(claim, { skip: !claim });

  const {
    data: meta,
    isLoading: metaLoading,
    isError: metaError,
  } = useGetSignupMetaQuery(undefined, {
    skip: !claim || claimError || !claimData?.self_signup_enabled,
  });

  const [startSignup, { isLoading: isSubmitting }] = useStartSignupMutation();

  const [companyName, setCompanyName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [country, setCountry] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [website, setWebsite] = useState("");
  const [fieldError, setFieldError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [prefillApplied, setPrefillApplied] = useState(false);

  const turnstileRef = useRef(null);
  const widgetIdRef = useRef(null);
  const siteKey = meta?.turnstile_site_key || "";
  const privacyUrl = meta?.privacy_url || "https://orderlink.io/privacy-policy";
  const shopLabel = claimData?.shop_name || claimData?.shop || "your Shopify store";

  useEffect(() => {
    if (prefillApplied) return;
    const prefill = claimData?.prefill;
    if (!prefill) return;
    setCompanyName(prefill.company_name || "");
    setEmail(prefill.email || "");
    setCountry(prefill.country || "");
    setPrefillApplied(true);
  }, [claimData?.prefill, prefillApplied]);

  useEffect(() => {
    if (!siteKey || !turnstileRef.current) return undefined;

    const renderWidget = () => {
      if (!window.turnstile || !turnstileRef.current) return;
      if (widgetIdRef.current != null) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (_) {
          /* ignore */
        }
        widgetIdRef.current = null;
      }
      widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
        sitekey: siteKey,
        callback: token => setTurnstileToken(token || ""),
        "expired-callback": () => setTurnstileToken(""),
        "error-callback": () => setTurnstileToken(""),
      });
    };

    const existing = document.querySelector(`script[src="${TURNSTILE_SCRIPT}"]`);
    if (window.turnstile) {
      renderWidget();
    } else if (existing) {
      existing.addEventListener("load", renderWidget);
    } else {
      const script = document.createElement("script");
      script.src = TURNSTILE_SCRIPT;
      script.async = true;
      script.onload = renderWidget;
      document.body.appendChild(script);
    }

    return () => {
      if (widgetIdRef.current != null && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (_) {
          /* ignore */
        }
        widgetIdRef.current = null;
      }
    };
  }, [siteKey]);

  const goWelcome = () =>
    navigate(claim ? `/shopify/welcome?claim=${encodeURIComponent(claim)}` : "/login");

  const handleSubmit = async e => {
    e.preventDefault();
    setFieldError("");

    if (!acceptPrivacy) {
      setFieldError("You must accept the Privacy Policy to continue.");
      return;
    }
    if (password.length < 8) {
      setFieldError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setFieldError("Password and Confirm Password didn't match.");
      return;
    }
    if (siteKey && !turnstileToken) {
      setFieldError("Please complete the captcha.");
      return;
    }

    try {
      const result = await startSignup({
        claim_id: claim,
        company_name: companyName.trim(),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        country: country.trim(),
        password,
        accept_privacy: true,
        form_loaded_at: meta?.form_loaded_at || "",
        turnstile_token: turnstileToken || "",
        website,
      }).unwrap();
      setSuccessMessage(result?.detail || "Check your email to verify your account.");
    } catch (err) {
      const data = err?.data;
      const detail = data?.detail;
      const fieldMsg =
        typeof detail === "string"
          ? detail
          : detail && typeof detail === "object"
            ? Object.values(detail).flat().filter(Boolean)[0]
            : null;
      const message =
        (typeof fieldMsg === "string" && fieldMsg) ||
        (typeof data === "object" &&
          Object.values(data || {})
            .flat()
            .find(v => typeof v === "string")) ||
        "Unable to start signup. Please try again.";
      setFieldError(message);
      notify({ type: "error", message });
    }
  };

  if (!claim || claimError || (claimData && !claimData.self_signup_enabled) || metaError) {
    return (
      <StatusScreen
        title="Signup unavailable"
        description="Open the app again from Shopify admin, or log in to link an existing account."
        action={<ButtonT1 title="Back" $variant="secondary" onClickCallback={goWelcome} />}
      />
    );
  }

  if (claimLoading || metaLoading || !claimData || !meta) {
    return (
      <StatusScreen
        title="Preparing your account"
        description="Verifying your Shopify install and loading the signup form."
      />
    );
  }

  if (successMessage) {
    return (
      <StatusScreen
        title="Check your email"
        description={successMessage}
      />
    );
  }

  return (
    <SignupShellDiv>
      <SignupPanelDiv>
        <SignupBrandRowDiv>
          <div className="brandMark">
            <img src={shopifyBag} alt="" />
            <div className="brandText">
              <span className="brandEyebrow">Orderlink × Shopify</span>
              <span className="brandTitle">New account</span>
            </div>
          </div>
          <SignupShopPillSpan title={shopLabel}>{shopLabel}</SignupShopPillSpan>
        </SignupBrandRowDiv>

        <SignupHeadingDiv>
          <h1>Create your Orderlink account</h1>
          <p>Connect {shopLabel} and finish setup in a few minutes.</p>
        </SignupHeadingDiv>

        <SignupFormDiv onSubmit={handleSubmit}>
          <div className="sectionLabel">Company</div>
          <div className="field">
            <label htmlFor="signup-company">Company name</label>
            <input
              id="signup-company"
              value={companyName}
              onChange={e => setCompanyName(e.target.value)}
              required
              maxLength={100}
              autoComplete="organization"
            />
          </div>

          <div className="sectionLabel">You</div>
          <div className="row">
            <div className="field">
              <label htmlFor="signup-first">First name</label>
              <input
                id="signup-first"
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                required
                maxLength={100}
                autoComplete="given-name"
              />
            </div>
            <div className="field">
              <label htmlFor="signup-last">Last name</label>
              <input
                id="signup-last"
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                required
                maxLength={100}
                autoComplete="family-name"
              />
            </div>
          </div>

          <div className="row">
            <div className="field">
              <label htmlFor="signup-email">Work email</label>
              <input
                id="signup-email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                maxLength={254}
                autoComplete="email"
              />
            </div>
            <div className="field">
              <label htmlFor="signup-country">Country</label>
              <input
                id="signup-country"
                value={country}
                onChange={e => setCountry(e.target.value)}
                maxLength={100}
                autoComplete="country-name"
              />
            </div>
          </div>

          <div className="sectionLabel">Security</div>
          <div className="row">
            <div className="field">
              <label htmlFor="signup-password">
                Password <span className="hint">min 8 characters</span>
              </label>
              <input
                id="signup-password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                minLength={8}
                maxLength={128}
                autoComplete="new-password"
              />
            </div>
            <div className="field">
              <label htmlFor="signup-confirm">Confirm password</label>
              <input
                id="signup-confirm"
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                required
                maxLength={128}
                autoComplete="new-password"
              />
            </div>
          </div>

          <input
            className="honeypot"
            type="text"
            name="website"
            value={website}
            onChange={e => setWebsite(e.target.value)}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
          />

          <label className="privacy">
            <input
              type="checkbox"
              checked={acceptPrivacy}
              onChange={e => setAcceptPrivacy(e.target.checked)}
            />
            <span>
              I agree to the{" "}
              <a href={privacyUrl} target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>
            </span>
          </label>

          {siteKey ? <div className="turnstileSlot" ref={turnstileRef} /> : null}

          <FormFieldError message={fieldError} />

          <div className="actions">
            <div className="primaryWrap">
              <ButtonT1
                type="submit"
                title="Create account"
                loading={isSubmitting}
                disabled={isSubmitting}
              />
            </div>
            <button
              type="button"
              className="backLink"
              onClick={goWelcome}
              disabled={isSubmitting}
            >
              Back to welcome
            </button>
          </div>
        </SignupFormDiv>
      </SignupPanelDiv>
    </SignupShellDiv>
  );
};
